'use client';

import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import GoogleAutocompleteInput from '@/components/GoogleMaps/GoogleAutoCompleteInput';

// Sólo campos Essentials: el mapa usa la ubicación y nada más.
const PLACE_FIELDS = ['location', 'formattedAddress'];

/**
 * Buscador de la ruta /conoce-los-puntos-hop.
 * Combina el input con autocompletado de Google Places y un botón de geolocalización.
 *
 * @param {Function} onAddressSelect - Recibe { lat, lng, address }
 * @param {Function} onGeoLocate - Recibe { lat, lng }
 * @param {Object} dict
 */
const Buscador = ({
  onAddressSelect,
  onGeoLocate,
  onPointSearch,
  onClearPointSearch,
  initialPointSearchQuery = '',
  dict,
  inputId = 'puntos-envios-autocomplete',
  resetVersion = 0
}) => {
  const copy = dict?.shipping_points || {};
  const autocompleteRef = useRef(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(false);
  const [pointSearchQuery, setPointSearchQuery] = useState(initialPointSearchQuery);
  const [searchMode, setSearchMode] = useState(initialPointSearchQuery ? 'point' : 'address');

  useEffect(() => {
    if (autocompleteRef.current) autocompleteRef.current.value = '';
    setGeoError(false);
    setPointSearchQuery(initialPointSearchQuery);
    setSearchMode(initialPointSearchQuery ? 'point' : 'address');
  }, [resetVersion, initialPointSearchQuery]);

  const handlePlaceSelect = (place) => {
    if (!place?.location) return;
    setSearchMode('address');
    onAddressSelect({
      lat: place.location.lat,
      lng: place.location.lng,
      address: place.formattedAddress || ''
    });
  };

  const handleGeolocate = () => {
    setSearchMode('address');
    if (!navigator.geolocation) {
      setGeoError(true);
      return;
    }

    setGeoLoading(true);
    setGeoError(false);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLoading(false);
        onGeoLocate({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        setGeoLoading(false);
        setGeoError(true);
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  };

  const handlePointSearch = (event) => {
    event.preventDefault();
    const query = pointSearchQuery.trim();
    if (query) onPointSearch?.(query);
  };

  const handleAddressMode = () => {
    setSearchMode('address');
    setPointSearchQuery('');
    onClearPointSearch?.();
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <div role="group" aria-label="Tipo de búsqueda" className="inline-flex self-start rounded-full bg-white/70 p-2">
        <button
          type="button"
          aria-pressed={searchMode === 'address'}
          onClick={handleAddressMode}
          className={`rounded-full px-12 py-6 text-12 font-semibold transition-colors ${
            searchMode === 'address' ? 'bg-cyan-dark text-white' : 'text-cyan-dark hover:bg-cyan-dark/10'
          }`}
        >
          Buscar por dirección
        </button>
        <button
          type="button"
          aria-pressed={searchMode === 'point'}
          onClick={() => setSearchMode('point')}
          className={`rounded-full px-12 py-6 text-12 font-semibold transition-colors ${
            searchMode === 'point' ? 'bg-cyan-dark text-white' : 'text-cyan-dark hover:bg-cyan-dark/10'
          }`}
        >
          Buscar por nombre o ID
        </button>
      </div>

      <div className="flex w-full flex-col items-stretch gap-8 md:flex-row md:items-center">
        {searchMode === 'address' ? (
          <div className="min-w-0 flex-1">
            <GoogleAutocompleteInput
              onPlaceSelect={handlePlaceSelect}
              dict={dict}
              autocompleteRef={autocompleteRef}
              id={inputId}
              label={null}
              placeholder={copy.address_placeholder}
              countryCode={dict?.country_short_name}
              fields={PLACE_FIELDS}
              center={dict?.map ? { lat: dict.map.lat, lng: dict.map.lng } : undefined}
            />
          </div>
        ) : (
          <form onSubmit={handlePointSearch} className="flex min-w-0 flex-1 items-center gap-8">
            <input
              type="search"
              value={pointSearchQuery}
              onChange={(event) => setPointSearchQuery(event.target.value)}
              maxLength={100}
              aria-label="Buscar por nombre de fantasía o ID de agencia"
              placeholder={copy.point_placeholder}
              className="min-w-0 flex-1 border-0 border-b border-cyan-dark/25 bg-transparent px-8 py-10 text-12 text-cyan-dark normal-case outline-none placeholder:text-cyan-dark/50 placeholder:normal-case focus:border-cyan-dark sm:text-14"
            />
            <button
              type="submit"
              aria-label="Buscar puntos por nombre o ID de agencia"
              title="Buscar puntos"
              disabled={!pointSearchQuery.trim()}
              className="inline-flex h-40 shrink-0 items-center gap-6 rounded-full bg-cyan-dark px-12 text-12 font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MagnifyingGlassIcon aria-hidden="true" className="h-16 w-16" />
              <span>Buscar</span>
            </button>
          </form>
        )}

        <button
          onClick={handleGeolocate}
          disabled={geoLoading}
          className="shrink-0 inline-flex items-center justify-center gap-6 px-16 py-12 rounded-full focus-visible:rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-dark bg-cyan-dark text-white text-12 font-semibold uppercase hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {geoLoading ? (
            <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
              <path fill="currentColor" d="M12 2a10 10 0 0110 10h-3a7 7 0 00-7-7V2z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" opacity="0.4" />
              <path fill="currentColor" d="M12 8a4 4 0 100 8 4 4 0 000-8z" />
              <circle cx="12" cy="12" r="1.5" fill="currentColor" />
            </svg>
          )}
          <span>Usar mi ubicación</span>
        </button>
      </div>

      {geoError && (
        <p role="alert" className="text-12 text-red">
          {copy.geo_error}
        </p>
      )}
    </div>
  );
};

export default Buscador;